import React from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { SUGGESTED_ENRICHMENT_PROMPTS } from "../../helper/suggestedEnrichmentPrompts";

const PURPLE = "#4A2CF5";
const PURPLE_FILL = "rgba(74, 44, 245, 0.12)";
/** Lighter than Enrich input stroke (~0.14–0.16). */
const GRAY_STROKE_LIGHT = "rgba(88, 89, 91, 0.1)";
const GRAY_STROKE_DARK = "rgba(255, 255, 255, 0.1)";
const GRAY_COPY_LIGHT = "#6B6C70";
const GRAY_COPY_DARK = "rgba(255, 255, 255, 0.62)";
const SELECTED_TEXT_LIGHT = "#171717";
const SELECTED_TEXT_DARK = "#FFFFFF";

/**
 * Toggleable suggested-prompt chips for Enrich Workspace.
 * Compact wrap — full labels, no ellipsis.
 *
 * States (V1 + V2):
 *   Default  — gray stroke, no fill, gray copy
 *   Hover    — white fill, lift + purple stroke
 *   Selected — purple fill, purple stroke, medium black text
 */
export default function SuggestedEnrichmentPrompts({
  prompts = SUGGESTED_ENRICHMENT_PROMPTS,
  selectedId = null,
  onSelect,
  disabled = false,
  /** "center" under input; "start" when sitting beside the input */
  align = "start",
  /** "wrap" (default) or "carousel" (single horizontal scrolling row) */
  layout = "wrap",
  /** Keep chip labels on one line so chips wrap as units (mobile enrich panel). */
  chipLabelNoWrap = false,
  gap: gapProp,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const grayStroke = isDark ? GRAY_STROKE_DARK : GRAY_STROKE_LIGHT;
  const grayCopy = isDark ? GRAY_COPY_DARK : GRAY_COPY_LIGHT;
  const selectedText = isDark ? SELECTED_TEXT_DARK : SELECTED_TEXT_LIGHT;
  const isCarousel = layout === "carousel";

  return (
    <Box
      data-testid="suggested-enrichment-prompts"
      role="group"
      aria-label="Suggested prompts"
      sx={{
        display: "flex",
        flexWrap: isCarousel ? "nowrap" : "wrap",
        justifyContent: align === "center" ? "center" : "flex-start",
        alignContent: "flex-start",
        alignItems: "center",
        gap: gapProp ?? 0.85,
        width: "100%",
        maxWidth: "100%",
        pt: 0.25,
        pb: 0.25,
        ...(isCarousel
          ? {
              overflowX: "auto",
              overflowY: "hidden",
              pb: 0.25,
              scrollbarWidth: "thin",
              scrollbarColor: isDark
                ? "rgba(255,255,255,0.25) transparent"
                : "rgba(88,89,91,0.28) transparent",
              "&::-webkit-scrollbar": { height: 5 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 999,
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.25)"
                  : "rgba(88,89,91,0.28)",
              },
            }
          : null),
      }}
    >
      {prompts.map((prompt) => {
        const selected = prompt.id === selectedId;
        return (
          <ButtonBase
            key={prompt.id}
            disabled={disabled}
            aria-pressed={selected}
            data-testid={`suggested-prompt-${prompt.id}`}
            onClick={() => onSelect?.(prompt)}
            sx={{
              flex: isCarousel || chipLabelNoWrap ? "0 0 auto" : "0 1 auto",
              width: "auto",
              maxWidth: "100%",
              alignSelf: "flex-start",
              justifyContent: "center",
              px: 1.25,
              py: 0.6,
              minHeight: 30,
              borderRadius: "999px",
              border: selected
                ? `1px solid ${PURPLE}`
                : `1px solid ${grayStroke}`,
              backgroundColor: selected ? PURPLE_FILL : "transparent",
              color: selected ? selectedText : grayCopy,
              boxShadow: "none",
              transition:
                "background-color 150ms ease, border-color 150ms ease, color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
              opacity: disabled ? 0.55 : 1,
              "&:hover": disabled
                ? undefined
                : {
                    transform: "translateY(-1px)",
                    borderColor: PURPLE,
                    backgroundColor: selected
                      ? PURPLE_FILL
                      : isDark
                        ? "rgba(255,255,255,0.92)"
                        : "#FFFFFF",
                    color: selected ? selectedText : grayCopy,
                  },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: 11.5,
                fontWeight: selected ? 500 : 400,
                lineHeight: 1.3,
                letterSpacing: "-0.01em",
                whiteSpace: isCarousel || chipLabelNoWrap ? "nowrap" : "normal",
                textAlign: "center",
                color: "inherit",
              }}
            >
              {prompt.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
